<template>
  <div>
    <page-nav-bar
      :is-sticky="true"
      :back-button="true"
      :has-fullscreen="true">
      <div
        class="column"
        slot="page-header">
        <div class="page-header-breadcrumb">
          <h1 class="is-padding-left page-header-breadcrumb-item">
            <span v-if="$route.name === 'StatsSales'">Statistiques de ventes</span>
            <span v-else-if="$route.name === 'StatsSellers'">Statistiques vendeurs</span>
            <span v-else-if="$route.name === 'StatsRdvs'">Statistiques de rendez-vous</span>
            <span v-else-if="$route.name === 'StatsMultidiffusion'">Analyse coût de communication</span>
          </h1>
        </div>
      </div>
    </page-nav-bar>
    <page-content
      :has-padd-right-normal="true"
      :has-padd-left-normal="true">
      <tabs
        :links="statsTabs"
        :level="1"
        class="columns is-sticky-in-mainColumn is-zindex-8"></tabs>
      <div class="has-background-white is-border is-margin-top">
        <div class="columns is-gapless">
          <div class="column is-3 is-border-right">
            <date-picker
              id="startDatePicker"
              :model="rawStartDate"
              :clearable="false"
              :field-class="'is-padding-left is-padding-right'"
              :input-class="'is-fullwidth is-borderless has-background-transparent'"
              @input="updateDateFilter({ start: $event })"></date-picker>
          </div>
          <div class="column is-3 is-border-right">
            <date-picker
              id="endDatePicker"
              :model="rawEndDate"
              :clearable="false"
              :field-class="'is-padding-left is-padding-right'"
              :input-class="'is-fullwidth is-borderless has-background-transparent'"
              @input="updateDateFilter({ end: $event })"></date-picker>
          </div>
          <div class="column is-3 is-padding-left is-padding-right">
            <div class="select is-fullwidth has-background-transparent">
              <select
                id="period"
                v-model="periodSelected"
                class="is-borderless is-shadowless">
                <option :value="null">
                  Périodes
                </option>
                <option
                  v-for="period in periodsDatas"
                  :value="period"
                  :key="'period' + period.value">
                  {{ period.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div
        class="is-padding-top-small is-padding-bottom-small is-padding-left-small is-border-left is-border-right"
        v-if="['StatsSales', 'StatsSellers'].includes($route.name)">
        <div class="field is-grouped">
          <p class="control">
            <label
              v-for="p in availableSaleStatusList"
              :for="'statusFilter-' + p.value"
              :key="p.value"
              :id="'status-' + p.value"
              class="checkbox checkRadioIsHide is-margin-right-xsmall">
              <input
                type="checkbox"
                v-model="filters.status"
                :id="'statusFilter-' + p.value"
                :value="p.value"
                :name="'status-' + p.value"
                @change="getStats()">
              <span
                class="tag"
                :class="p.value === 'canceled' ? 'is-danger' : 'is-' + p.value">
                <span>{{ p.label }}</span>
                <span class="icon">
                  <i
                    class="far"
                    :class="filters.status && filters.status.includes(p.value) ? 'fa-check-square' : 'fa-square'"></i>
                </span>
              </span>
            </label>
          </p>
        </div>
      </div>
      <stats-sales-graph
        v-if="$route.name === 'StatsSales'"
        :from="'Stats'"
        :total-sales="totalSales"
        :stats-sales="sortedSales"
        :stats-reference-sales="statsReferenceSales"
        :filters="filters"
        :reference-selected="referenceSelected"
        :total-reference-sales="totalReferenceSales"
        :reference-datas="referenceDatas"
        :flow-rate="flowRate"
        @updateFilters="updateFilters"
        @updateReference="updateReference"></stats-sales-graph>
      <router-view
        :key="$route.name"
        :source-stats-sales-pre-reservation="sortedSales.filter(x => x.status === 'pre_reserved')"
        :source-stats-sales-reservation="sortedSales.filter(x => x.status === 'reserved')"
        :source-stats-sales-sold="sortedSales.filter(x => x.status === 'sold')"
        :source-stats-sales-canceled="sortedSales.filter(x => x.status === 'canceled')"
        :source-stats-reference-sales-pre-reservation="statsReferenceSalesPreReservation"
        :source-stats-reference-sales-reservation="statsReferenceSalesReservation"
        :source-stats-reference-sales-sold="statsReferenceSalesSold"
        :source-stats-rdvs="statsRdvs"
        :source-flow-rate="flowRate"
        :source-stats-multidiffusion="statsMultidiffusion"
        :source-loading="loading"
        :source-sorted-params="sortedParams"
        @sort="sort"
        @updateStats="getStats"
        @updateSources="updateSources"></router-view>
    </page-content>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import { DefaultValues } from '@/mixins'
import StatsMixins from './StatsMixins'
import 'vue-swatches/dist/vue-swatches.min.css'

export default {
  name: 'StatsVue',
  components: {
    StatsSalesGraph: () => import('./StatsSales/StatsSalesGraph')
  },
  mixins: [StatsMixins, DefaultValues],
  data () {
    return {
      rawStartDate: moment().startOf('year').toDate(),
      rawEndDate: moment().endOf('day').toDate()
    }
  },
  computed: {
    ...mapGetters(['accountPk', 'canUseCalendar']),
    ...mapState(['currentUser']),
    currentStatsId () {
      return this.currentUser.account.pk
    },
    sellerRdvs () {
      const entityNameGenerator = (rdv) => `${rdv.user.first_name} ${rdv.user.last_name}`
      return this.computeEntityData(this.statsRdvs, 'user.pk', 'user', entityNameGenerator)
    },
    availableSaleStatusList () {
      return [
        { value: 'pre_reserved', label: 'Pré-Réservation' },
        { value: 'reserved', label: 'Réservation' },
        { value: 'sold', label: 'Acte' },
        { value: 'canceled', label: 'Annulation' }
      ]
    },
    statsTabs () {
      let tabs = [
        { link: 'StatsSales', label: 'Réservations/Ventes', icon: 'fas fa-medal' },
        { link: 'StatsSellers', label: 'Force de vente', icon: 'fas fa-users' }
        // { link: 'StatsMultidiffusion', label: 'Communication / Marketing', icon: 'fas fa-stream' }
      ]
      if (this.canUseCalendar) {
        tabs.push({ link: 'StatsRdvs', label: 'Rendez-vous', icon: 'fas fa-calendar-check' })
      }
      return tabs
    }
  },
  watch: {
    '$route.name' () {
      this.getStats()
    },
    periodSelected (v) {
      this.updateDateFilter(v)
    },
    accountPk () {
      this.getStats()
    }
  },
  mounted () {
    this.getFlowRate()
  },
  methods: {
    getStats (referenceMode) {
      if (!this.accountPk) return
      if (this.$route.name === 'StatsSellers') {
        this.getStatsSales(referenceMode)
      } else if (this.$route.name === 'StatsSales') {
        this.getStatsSales(referenceMode)
      } else if (this.$route.name === 'StatsRdvs') {
        this.getStatsRdvs(referenceMode)
      }
      // else if (this.$route.name === 'StatsMultidiffusion' && !this.statsSales.length) this.getStatsMultidiffusion()
    }
  }
}
</script>
